import React, { useCallback, useState } from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import { Button } from "antd";
import { getDataWithLabels } from "./helper/formHelpers";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedTenant,
  selectUser,
} from "../../../../../store/user/selectors";
import { selectSelectedChat } from "../../../../../store/chats/selectors";
import { FILE_BASE_URL } from "../../../../../config";
import { createFilesMessagePayload } from "../../ChatWindow/helper";
import { sendWebSocketMessage } from "../../../../../store/chats/slice";
import { getFullName } from "../../../../../utils/shared";

const styles = StyleSheet.create({
  page: {
    padding: 36,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
    color: "#1a202c",
  },

  header: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
    paddingBottom: 14,
    borderBottomWidth: 2,
    borderBottomColor: "#7b3bea",
  },
  logo: {
    width: 56,
    height: 56,
    borderRadius: 6,
    marginRight: 14,
    objectFit: "contain",
  },
  headerText: { flex: 1 },
  businessName: {
    fontSize: 18,
    fontFamily: "Helvetica-Bold",
    color: "#1a202c",
    marginBottom: 2,
  },
  branchName: {
    fontSize: 11,
    color: "#7b3bea",
    fontFamily: "Helvetica-Bold",
    marginBottom: 2,
  },
  branchAddress: { fontSize: 9, color: "#718096" },
  topicName: {
    marginTop: 4,
    fontSize: 10,
    color: "#2d3748",
    fontFamily: "Helvetica-BoldOblique",
  },

  // ── Section card
  section: {
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 6,
    overflow: "hidden",
  },
  sectionTitle: {
    backgroundColor: "#7b3bea",
    color: "#ffffff",
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    padding: "6 10",
    letterSpacing: 0.5,
  },
  sectionBody: { padding: "8 10" },

  // ── Table (array of objects)
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#edf2f7",
    paddingVertical: 5,
  },
  tableRowEven: { backgroundColor: "#f7fafc" },
  tableKey: {
    width: "40%",
    color: "#4a5568",
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    paddingRight: 6,
  },
  tableValue: { width: "60%", color: "#1a202c", fontSize: 9 },

  // ── Grid (plain object)
  grid: { flexDirection: "row", flexWrap: "wrap", marginTop: 4 },
  gridItem: { width: "50%", paddingVertical: 4, paddingRight: 8 },
  gridKey: {
    color: "#4a5568",
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    marginBottom: 1,
  },
  gridValue: { color: "#1a202c", fontSize: 9 },

  // ── Divider between array entries
  entryDivider: {
    borderTopWidth: 1,
    borderTopColor: "#bee3f8",
    marginVertical: 6,
  },

  // ── Footer
  footer: {
    position: "absolute",
    bottom: 24,
    left: 36,
    right: 36,
    borderTopWidth: 1,
    borderTopColor: "#e2e8f0",
    paddingTop: 6,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: { fontSize: 8, color: "#a0aec0" },
});

/** Format any value into a readable string */
function formatValue(value) {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.join(", ");
  // ISO date  →  readable date (en-IN locale)
  if (typeof value === "string" && /\d{4}-\d{2}-\d{2}T/.test(value)) {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  return String(value);
}

/** Renders a plain object as a 2-column grid */
function ObjectGrid({ data }) {
  return (
    <View style={styles.grid}>
      {Object.entries(data).map(([key, value]) => (
        <View key={key} style={styles.gridItem}>
          <Text style={styles.gridKey}>{key}</Text>
          <Text style={styles.gridValue}>{formatValue(value)}</Text>
        </View>
      ))}
    </View>
  );
}

function ArrayTable({ data }) {
  return (
    <View>
      {data.map((item, idx) => (
        <View key={idx}>
          {idx > 0 && <View style={styles.entryDivider} />}
          <View>
            {Object.entries(item).map(([key, value], rowIdx) => (
              <View
                key={key}
                style={[
                  styles.tableRow,
                  rowIdx % 2 === 0 && styles.tableRowEven,
                ]}
              >
                <Text style={styles.tableKey}>{key}</Text>
                <Text style={styles.tableValue}>{formatValue(value)}</Text>
              </View>
            ))}
          </View>
        </View>
      ))}
    </View>
  );
}

/**
 * One section of forms_data.
 * Automatically picks ArrayTable for arrays, ObjectGrid for plain objects.
 */
function FormSection({ title, data }) {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title.toUpperCase()}</Text>
      <View style={styles.sectionBody}>
        {Array.isArray(data) ? (
          <ArrayTable data={data} />
        ) : (
          <ObjectGrid data={data} />
        )}
      </View>
    </View>
  );
}
function PDFDocument({ json }) {
  const {
    logo,
    businessName = "",
    branchName = "",
    branchAddress = "",
    topicName = "",
    forms_data = {},
  } = json;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* ── Header ── */}
        <View style={styles.header}>
          {logo && <Image style={styles.logo} src={logo} />}
          <View style={styles.headerText}>
            <Text style={styles.businessName}>{businessName}</Text>
            {branchName && branchName !== businessName && (
              <Text style={styles.branchName}>{branchName}</Text>
            )}
            {branchAddress ? (
              <Text style={styles.branchAddress}>{branchAddress}</Text>
            ) : null}
            {topicName ? (
              <Text style={styles.topicName}>{topicName}</Text>
            ) : null}
          </View>
        </View>

        {/* ── Dynamic forms_data sections ── */}
        {Object.entries(forms_data).map(([sectionName, sectionData]) => (
          <FormSection
            key={sectionName}
            title={sectionName}
            data={sectionData}
          />
        ))}

        {/* ── Footer ── */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{businessName}</Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
}

const PDFGenerator = ({ form, template }) => {
  const [loading, setLoading] = useState(false);

  const tenant = useSelector(selectSelectedTenant);
  const chat = useSelector(selectSelectedChat);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();

  const handleClick = useCallback(async () => {
    setLoading(true);
    try {
      const data = await form.validateFields();

      const json = {
        logo: `${FILE_BASE_URL}${tenant?.logo}`,
        businessName: tenant?.name,
        branchName: chat?.branch?.name,
        branchAddress: chat?.branch?.address,
        topicName: chat?.topic?.name,
        forms_data: getDataWithLabels({
          data,
          template,
        }),
      };

      const blob = await pdf(<PDFDocument json={json} />).toBlob();

      const fileName = `${template?.name || "document"} Report.pdf`;

      const file = new File([blob], fileName, {
        type: "application/pdf",
      });

      // ✅ send via websocket
      dispatch(
        sendWebSocketMessage(
          //@ts-ignore
          createFilesMessagePayload({
            selectedChat: chat,
            user,
            files: [
              {
                id: `${Date.now()}`,
                file,
                filename: file.name,
                file_size_bytes: file.size,
                mime_type: file.type,
                message:
                  `${fileName} PDF generated by ${getFullName(user)}`.trim(),
              },
            ],
          }),
        ),
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.log("🚀 ~ PDFGenerator.jsx:272 ~ PDFGenerator ~ error:", error);
    } finally {
      setLoading(false);
    }
  }, [template, tenant, chat]);

  return (
    <Button
      onClick={handleClick}
      disabled={loading}
      type="primary"
      style={{ marginRight: "10px" }}
      loading={loading}
    >
      {loading ? "Generating…" : "Generate PDF"}
    </Button>
  );
};

export default PDFGenerator;
